import { Link } from "react-router-dom";
import { Phone, CalendarCheck, Flame, ShieldCheck, BadgeDollarSign, Clock4, Award, ThumbsUp, Wrench, Droplets, Check, Camera, Waves, Leaf, Zap } from "lucide-react";
import { Seo, faqLd } from "../components/Seo";
import { Img } from "../lib/img";
import { BUSINESS, IMAGES, HOME_SERVICES, DIFFERENCE } from "../content/site";
import HOME_FAQS from "../content/generated/home-faqs.json";
import {
  SectionHead, ServiceCard, EmergencyBanner, PlansSection, SpecialsSection, ReviewsSection, AreasSection, FaqList, ScheduleSection,
} from "../components/sections/Blocks";


const Hero = () => (
  <section className="relative isolate overflow-hidden bg-plum-deep" aria-labelledby="home-h1" data-testid="home-hero">
    <Img src={IMAGES.hero} alt="Plumber repairing pipes in an Asheville home" eager priority widths={[480, 768, 1024, 1600, 2000]} sizes="100vw" className="absolute inset-0 -z-20 h-full w-full object-cover object-[75%_center]" />
    <div className="absolute inset-0 -z-10 bg-gradient-to-r from-plum-deep via-plum-deep/90 to-plum-deep/30 lg:to-transparent" aria-hidden="true" />
    <div className="container-x grid items-end gap-10 pb-0 pt-14 lg:grid-cols-12 lg:pt-24">
      <div className="pb-14 text-white lg:col-span-7 lg:pb-24">
        <h1 id="home-h1" className="inline-flex items-center gap-2 rounded-full bg-white/10 px-4 py-1.5 text-xs font-semibold uppercase tracking-[0.14em] text-gold ring-1 ring-white/20 sm:text-sm">
          Plumbing Services in Asheville, NC | PlumbSmart
        </h1>
        <p className="mt-6 font-display text-4xl font-extrabold leading-[1.05] tracking-tight sm:text-5xl lg:text-6xl" data-testid="hero-heading">
          Asheville's Trusted <span className="text-gold">Plumbing &amp; Drain</span> Experts
        </p>
        <p className="mt-6 max-w-xl text-base leading-relaxed text-white/85 md:text-lg">
          From emergency plumbing and drain cleaning to water heater replacements and sewer repairs, PlumbSmart helps Asheville homeowners and businesses with professional plumbing solutions.
        </p>
        <div className="mt-8 grid gap-3 sm:flex sm:flex-wrap">
          <Link to="/schedule-service" className="btn-gold px-7 py-4 text-base" data-testid="hero-schedule-service-btn"><CalendarCheck className="h-5 w-5" aria-hidden="true" />Schedule Service</Link>
          <Link to="/free-water-heater-replacement" className="btn-ghost-light px-7 py-4 text-base" data-testid="hero-water-heater-estimate-btn"><Flame className="h-5 w-5" aria-hidden="true" />Free Water Heater Estimate</Link>
        </div>
        <a href={BUSINESS.phoneHref} className="mt-6 inline-flex items-center gap-3 text-white" data-testid="hero-call-btn">
          <span className="grid h-11 w-11 place-items-center rounded-full bg-gold text-plum"><Phone className="h-5 w-5" aria-hidden="true" /></span>
          <span><span className="block text-xs text-white/70">Call now, family owned since {BUSINESS.since}</span><span className="font-display text-2xl font-bold">{BUSINESS.phone}</span></span>
        </a>
      </div>
      <div className="relative -mt-6 self-end lg:col-span-5 lg:mt-0">
        <Img src={BUSINESS.van} alt="PlumbSmart Plumbing & Drains service van with logo and cartoon plumber mascot" widths={[480, 700, 1000]} sizes="(min-width:1024px) 40vw, 384px" width="1448" height="715" className="relative z-10 mx-auto w-full max-w-sm drop-shadow-2xl sm:max-w-md lg:max-w-none" />
      </div>
    </div>
  </section>
);

const TRUST = [
  { icon: Clock4, title: "Emergency service", text: "Plumbing emergencies handled when you need it most." },
  { icon: BadgeDollarSign, title: "Set pricing", text: "Upfront pricing with options on all repairs." },
  { icon: ThumbsUp, title: "100% satisfaction", text: "Backed by our satisfaction guarantee." },
  { icon: ShieldCheck, title: "Licensed & insured", text: `Family owned · ${BUSINESS.license}` },
  { icon: Award, title: "PHCC member", text: "Part of the Plumbing-Heating-Cooling Contractors association." },
];

const TrustBar = () => (
  <section className="relative z-10 border-b border-gray-100 bg-white" aria-label="Why homeowners trust PlumbSmart" data-testid="trust-bar">
    <div className="container-x grid grid-cols-2 gap-x-4 gap-y-6 py-8 md:grid-cols-3 lg:grid-cols-5">
      {TRUST.map(({ icon: I, title, text }, i) => (
        <div key={title} className={`flex flex-col gap-2 sm:flex-row sm:gap-3 ${i === TRUST.length - 1 ? "col-span-2 md:col-span-1" : ""}`}>
          <span className="grid h-11 w-11 shrink-0 place-items-center rounded-xl bg-plum-soft text-plum"><I className="h-5 w-5" aria-hidden="true" /></span>
          <div><p className="font-display font-bold text-ink">{title}</p><p className="text-sm text-ink-soft">{text}</p></div>
        </div>
      ))}
    </div>
  </section>
);

const ServicesGrid = () => (
  <section className="section bg-mist" aria-labelledby="services-title" data-testid="services-grid-section">
    <div className="container-x">
      <SectionHead eyebrow="Plumbing services" title={<span id="services-title">Plumbing, drain and water heater services in Asheville</span>} text="Residential and commercial plumbing, handled by one local team since 2008." />
      <div className="mt-8 grid gap-3 sm:mt-12 sm:grid-cols-2 sm:gap-6 lg:grid-cols-4">
        {HOME_SERVICES.map((s) => <ServiceCard key={s.path} {...s} />)}
      </div>
    </div>
  </section>
);

const WhyChoose = () => (
  <section className="section bg-white" aria-labelledby="why-title" data-testid="why-choose-section">
    <div className="container-x grid gap-14 lg:grid-cols-2">
      <div>
        <SectionHead eyebrow="Why choose PlumbSmart" title={<span id="why-title">The PlumbSmart difference</span>} />
        <p className="mt-5 leading-relaxed text-ink-soft">
          In July 2008 PlumbSmart started with a simple idea: provide good quality plumbing to customers while explaining their options, so they have peace of mind and know they're getting good value for their money. We're family owned and operated, licensed and insured, with a passion for your plumbing.
        </p>
        <div className="mt-8 flex flex-wrap gap-3">
          <Link to="/why-choose-plumbsmart" className="btn-plum" data-testid="why-learn-more-btn">Why choose us</Link>
          <Link to="/about" className="btn-outline" data-testid="why-about-btn">Our story</Link>
        </div>
      </div>
      <ul className="grid gap-4 sm:grid-cols-2">
        {DIFFERENCE.map((d, i) => (
          <li key={d} className={`flex items-center gap-3 rounded-2xl p-5 ${i % 3 === 0 ? "bg-plum text-white" : "bg-mist text-ink"}`}>
            <Check className={`h-6 w-6 shrink-0 ${i % 3 === 0 ? "text-gold" : "text-plum"}`} aria-hidden="true" />
            <span className="font-semibold">{d}</span>
          </li>
        ))}
      </ul>
    </div>
  </section>
);

const WaterHeaterHighlight = () => (
  <section className="section bg-mist" aria-labelledby="wh-title" data-testid="water-heater-highlight">
    <div className="container-x grid items-center gap-14 lg:grid-cols-2">
      <div className="relative">
        <Img src={BUSINESS.warrantyAd} alt="PlumbSmart 5 to 10 year water heater warranty graphic" widths={[600, 900, 1200]} sizes="(min-width:1024px) 50vw, 100vw" width="1448" height="1086" className="w-full rounded-3xl bg-white" />
      </div>
      <div>
        <SectionHead eyebrow="Water heater repair & replacement" title={<span id="wh-title">5 to 10 year warranties on every water heater we install</span>} />
        <p className="mt-5 leading-relaxed text-ink-soft">A water heater is a significant investment in your home's comfort. When you choose PlumbSmart for your water heater installation, it's backed by our 5 to 10 year water heater warranty, which we're proud to say no other company in the region offers.</p>
        <ul className="mt-6 grid gap-3 sm:grid-cols-2">
          {["No hot water or lukewarm water", "Leaks or rust around the tank", "Popping or rumbling noises", "Pilot or ignition problems"].map((t) => (
            <li key={t} className="flex gap-2 text-ink"><Wrench className="mt-0.5 h-5 w-5 text-plum" aria-hidden="true" />{t}</li>
          ))}
        </ul>
        <Img src={BUSINESS.brandsStrip} alt="Water heater brands we service: A. O. Smith, Rinnai, Rheem, Takagi, Navien, State, American, U.S. Craftmaster, American Standard" widths={[600, 1000]} sizes="(min-width:1024px) 40vw, 90vw" className="mt-8 w-full max-w-lg" />
        <div className="mt-8 flex flex-wrap gap-3">
          <Link to="/free-water-heater-replacement" className="btn-gold" data-testid="wh-estimate-btn">Free Water Heater Estimate</Link>
          <Link to="/water-heater-repair" className="btn-outline" data-testid="wh-repair-btn">Water Heater Repair</Link>
        </div>
      </div>
    </div>
  </section>
);

const EfficiencyHighlight = () => (
  <section className="section bg-white" aria-labelledby="eff-title" data-testid="tankless-heat-pump-highlight">
    <div className="container-x">
      <SectionHead eyebrow="High-efficiency hot water" title={<span id="eff-title">Tankless and heat pump water heaters</span>} text="Thinking past a standard tank? We'll help you compare options for your household's hot water use and space." />
      <div className="mt-12 grid gap-6 lg:grid-cols-2">
        {[
          { name: "Tankless Water Heaters", path: "/tankless-water-heaters", img: "tankless_water_heater", icon: Zap, text: "Heats water on demand instead of storing it, freeing up floor space and ending the wait for a tank to recover.", alt: "Tankless water heater mounted on a wall" },
          { name: "Hybrid Heat Pump Water Heaters", path: "/hybrid-heat-pump-water-heaters", img: "heat_pump_water_heater", icon: Leaf, text: "Pulls heat from the surrounding air to warm your water, an efficient alternative to a conventional electric tank.", alt: "Hybrid heat pump water heater in a basement" },
        ].map(({ name, path, img, icon: I, text, alt }) => (
          <article key={path} className="group relative overflow-hidden rounded-3xl bg-plum-deep" data-testid={`efficiency-card-${path.slice(1)}`}>
            <Img src={IMAGES[img]} alt={alt} widths={[640, 1000, 1400]} sizes="(min-width:1024px) 50vw, 100vw" className="absolute inset-0 h-full w-full object-cover opacity-40 transition-transform duration-700 group-hover:scale-105" />
            <div className="relative flex min-h-[340px] flex-col justify-end p-8 text-white">
              <I className="h-8 w-8 text-gold" aria-hidden="true" />
              <h3 className="mt-4 text-2xl font-bold">{name}</h3>
              <p className="mt-2 max-w-md text-white/85">{text}</p>
              <Link to={path} className="btn-gold mt-6 self-start" data-testid={`efficiency-link-${path.slice(1)}`}>Learn More<span className="sr-only"> about {name}</span></Link>
            </div>
          </article>
        ))}
      </div>
    </div>
  </section>
);

const DrainSewer = () => (
  <section className="section bg-mist" aria-labelledby="drain-title" data-testid="drain-sewer-section">
    <div className="container-x grid gap-14 lg:grid-cols-12">
      <div className="lg:col-span-5">
        <SectionHead eyebrow="Drain cleaning & sewer" title={<span id="drain-title">Clear drains and healthy sewer lines</span>} text="Every drain clearing ends with a free camera inspection, so you can see the line is open." />
        <Img src={IMAGES.drain_cleaning} alt="Drain cleaning equipment used by a PlumbSmart plumber" widths={[600, 900]} sizes="(min-width:1024px) 40vw, 100vw" className="mt-8 aspect-[16/9] w-full rounded-3xl object-cover sm:aspect-[4/3]" />
      </div>
      <div className="grid grid-cols-2 gap-3 sm:gap-5 lg:col-span-7">
        {[
          { icon: Droplets, name: "Drain Cleaning", path: "/drain-cleaning", text: "Kitchen, bath, laundry and floor drains cleared of grease, hair, soap and debris." },
          { icon: Waves, name: "Sewer Jet Cleaning", path: "/sewer-jet-cleaning", text: "High-pressure water jetting scours buildup and roots from the pipe walls." },
          { icon: Camera, name: "Video Camera Inspections", path: "/video-line-inspections", text: "See cracks, bellies, roots and blockages on screen before deciding on repairs." },
          { icon: Wrench, name: "Sewer Line Services", path: "/sewer-lines", text: "Sewer line diagnosis and repair for backups, odors and soggy spots in the yard." },
          { icon: Droplets, name: "Sink, Shower & Toilet Backups", path: "/sink-shower-toilet-backups", text: "Backed-up fixtures cleared fast, with the cause found so it doesn't return." },
          { icon: Leaf, name: "Septic Tank Services", path: "/septic-tank-repair-replacement", text: "Septic clearing, troubleshooting, repair and installation." },
        ].map(({ icon: I, name, path, text }) => (
          <Link key={path} to={path} className="group rounded-2xl border border-gray-100 bg-white p-4 transition-[box-shadow,transform] hover:-translate-y-1 hover:shadow-lg sm:p-6" data-testid={`drain-link-${path.slice(1)}`}>
            <I className="h-7 w-7 text-plum" aria-hidden="true" />
            <h3 className="mt-3 text-base font-bold leading-snug text-ink group-hover:text-plum sm:mt-4 sm:text-lg">{name}</h3>
            <p className="mt-1.5 hidden text-sm leading-relaxed text-ink-soft sm:block">{text}</p>
          </Link>
        ))}
      </div>
    </div>
  </section>
);

const HomeFaq = () => (
  <section className="section bg-white" aria-labelledby="home-faq-title" data-testid="home-faq-section">
    <div className="container-x grid gap-12 lg:grid-cols-12">
      <div className="lg:col-span-4">
        <SectionHead eyebrow="FAQ" title={<span id="home-faq-title">Asheville plumbing FAQ</span>} text="Straight answers to the questions we hear most from Asheville homeowners." />
        <Link to="/faqs" className="btn-outline mt-8" data-testid="home-faq-all-btn">All FAQs</Link>
      </div>
      <div className="lg:col-span-8"><FaqList faqs={HOME_FAQS} idPrefix="home-faq" /></div>
    </div>
  </section>
);

export default function HomePage() {
  return (
    <>
      <Seo title="Plumber Asheville, NC | Plumbing & Drain Services | PlumbSmart" path="/"
        description="PlumbSmart provides plumbing services in Asheville, NC, including emergency repairs, drain cleaning, sewer lines and water heaters. Schedule service today."
        jsonLd={[faqLd(HOME_FAQS)]} />
      <Hero />
      <TrustBar />
      <ServicesGrid />
      <EmergencyBanner />
      <WhyChoose />
      <WaterHeaterHighlight />
      <EfficiencyHighlight />
      <DrainSewer />
      <PlansSection />
      <SpecialsSection />
      <ReviewsSection />
      <AreasSection />
      <HomeFaq />
      <ScheduleSection />
    </>
  );
}
